import { useMemo } from "react";
import { BufferGeometry, Float32BufferAttribute } from "three";
import type { CenterlineData } from "./useTunnelCenterline";
import type { ProfileAssignment } from "./tunnelProfileShapes";
import { type WeaknessZoneLine, loadLines, lineToWorld3D } from "./weaknessZones";
import { useProject } from "./ProjectContext";

interface WeaknessZoneLines3DProps {
  data: CenterlineData;
  profileAssignments: ProfileAssignment[];
}

/**
 * Renders weakness zone lines (drawn in the 2D utbrettsprofil) projected onto
 * the 3D tunnel surface. Read-only visualization.
 */
export function WeaknessZoneLines3D({ data, profileAssignments }: WeaknessZoneLines3DProps) {
  const project = useProject();
  const lines = loadLines(project.id);

  const geometries = useMemo(() => {
    if (lines.length === 0) return [];

    const frameSamples = 500;
    const frames = data.curve.computeFrenetFrames(frameSamples, false);
    const curvePoints = data.curve.getSpacedPoints(frameSamples);

    return lines.map((line) => {
      const worldPts = lineToWorld3D(line, data, profileAssignments, frames, curvePoints, frameSamples);

      // Build line segment pairs for lineSegments geometry
      const verts: number[] = [];
      for (let i = 0; i < worldPts.length - 1; i++) {
        const a = worldPts[i];
        const b = worldPts[i + 1];
        verts.push(a.x, a.y, a.z, b.x, b.y, b.z);
      }

      const geo = new BufferGeometry();
      geo.setAttribute("position", new Float32BufferAttribute(verts, 3));
      return { geometry: geo, color: line.color };
    });
  }, [lines, data, profileAssignments]);

  return (
    <group>
      {geometries.map((g, i) => (
        <lineSegments key={i} geometry={g.geometry}>
          <lineBasicMaterial color={g.color} linewidth={2} />
        </lineSegments>
      ))}
    </group>
  );
}
